import { useEffect, useRef, useState } from "react";
import { ArrowLeft, BookOpen, Check, CheckCheck, Clock, Download, FileText, Loader2, MapPin, Paperclip, Send, StickyNote, TriangleAlert, UserRound, X } from "lucide-react";
import { cn } from "./ui";
import { MAX_UPLOAD_MB, WA_MEDIA_MB, conversationsApi, dayLabel, emailOf, fileUrl, hm, type Conversation, type ConversationFull, type Message } from "./inbox-api";
import { aed, zohoApi, type ZohoCustomer } from "./companies-api";
import { money } from "./deals-api";
import { apiErrorText } from "@/lib/api";
import { fillVars, type Snippet } from "./snippets-api";
import type { InboxRules } from "./inbox-rules-api";
import { locale, t } from "@/lib/i18n";
import { BazaAsk, useBazaEnabled } from "./baza-ask";

// Панель переписки окна «Входящие»: лента сообщений с вложениями и статусами доставки, ответ клиенту,
// внутренняя заметка (клиент её не видит), отправка файла, назначение ответственного и закрытие диалога.

export type SendKind = "out" | "note";
export type Member = { id: string; name: string; role: string };

const channelLabel = (c: string) => (c === "telegram" ? "Telegram" : c === "email" ? t("Почта") : "WhatsApp");
const channelIcon = (c: string) => (c === "telegram" ? "ri-telegram-line" : c === "email" ? "ri-mail-line" : c === "instagram" ? "ri-instagram-line" : "ri-whatsapp-line");

export const initials = (n: string) =>
  n
    .replace(/^\+/, "")
    .split(/\s+/)
    .filter((x) => /^[\p{L}\d]/u.test(x))
    .map((x) => x[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "•";

const size = (n?: number | null) => (!n ? "" : n < 1024 * 1024 ? t("{n} КБ", { n: Math.max(1, Math.round(n / 1024)) }) : t("{n} МБ", { n: (n / 1024 / 1024).toFixed(1) }));
const dur = (s?: number | null) => (!s ? "" : `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`);

function Status({ m }: { m: Message }) {
  if (m.status === "failed")
    return (
      <span className="inline-flex items-center gap-1 text-red-200" title={m.error ? t("Не отправлено: {error}", { error: m.error }) : t("Не отправлено")}>
        <TriangleAlert className="size-3" aria-hidden />
        {t("не отправлено")}
      </span>
    );
  if (m.status === "pending") return <Clock className="size-3 opacity-70" aria-label={t("Отправляется")} />;
  if (m.status === "sent") return <Check className="size-3 opacity-70" aria-label={t("Отправлено")} />;
  return <CheckCheck className={cn("size-3", m.status === "read" ? "text-sky-200" : "opacity-70")} aria-label={m.status === "read" ? t("Прочитано") : t("Доставлено")} />;
}

function Attachment({ m, out }: { m: Message; out: boolean }) {
  const media = m.media;
  if (!media) return null;
  const url = fileUrl(m);
  const tone = out ? "text-primary-foreground" : "text-foreground";
  if (media.kind === "image" && url)
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-[6px]">
        <img src={url} alt={media.name || t("Фото")} loading="lazy" className="max-h-64 w-auto max-w-full object-contain" />
      </a>
    );
  if (media.kind === "sticker" && url) return <img src={url} alt={t("Стикер")} loading="lazy" className="size-28 object-contain" />;
  if (media.kind === "video" && url) return <video src={url} controls preload="metadata" className="max-h-64 w-full rounded-[6px]" />;
  if (media.kind === "audio" && url)
    return (
      <span className="flex items-center gap-2">
        <audio src={url} controls preload="metadata" className="h-9 w-56 max-w-full" />
        {media.duration ? <span className="text-[10px] opacity-80">{dur(media.duration)}</span> : null}
      </span>
    );
  if (media.kind === "location")
    return (
      <a
        href={`https://maps.google.com/?q=${media.lat},${media.lng}`}
        target="_blank"
        rel="noreferrer"
        className={cn("inline-flex items-center gap-1.5 text-[12px] underline-offset-2 hover:underline", tone)}
      >
        <MapPin className="size-3.5" aria-hidden />
        {t("Геопозиция")}
      </a>
    );
  if (media.kind === "contact")
    return (
      <span className={cn("inline-flex items-center gap-1.5 text-[12px]", tone)}>
        <UserRound className="size-3.5" aria-hidden />
        {t("Контакт")}
      </span>
    );
  // документ или вложение без файла
  return (
    <a
      href={url ?? undefined}
      download={url ? media.name || true : undefined}
      target={url ? "_blank" : undefined}
      rel="noreferrer"
      className={cn(
        "flex items-center gap-2 rounded-[6px] px-2 py-1.5 text-[12px]",
        out ? "bg-primary-foreground/15" : "bg-background/70 ring-1 ring-border",
        !url && "opacity-70",
      )}
      title={url ? t("Скачать") : t("Файл недоступен: вложение из старой истории")}
    >
      {url ? <Download className="size-3.5 flex-none" aria-hidden /> : <FileText className="size-3.5 flex-none" aria-hidden />}
      <span className="min-w-0">
        <span className="block truncate font-medium">{media.name || media.kind}</span>
        <span className="block text-[10px] opacity-75">{url ? size(media.size) || media.mime : t("файл не сохранён")}</span>
      </span>
    </a>
  );
}

/** Системная отметка на языке интерфейса: сервер пишет текст по-русски, а в meta — структуру события. */
export function systemText(m: Message): string {
  const meta = (m.meta ?? {}) as { sys?: string; actor?: string; target?: string };
  const actor = meta.actor ?? "";
  const target = meta.target ?? "";
  if (meta.sys === "take") return t("{name} берёт диалог", { name: target || actor });
  if (meta.sys === "handoff") return t("{actor} передаёт диалог: {name}", { actor, name: target });
  if (meta.sys === "unassign") return t("{name} снимает ответственного", { name: actor });
  if (meta.sys === "salesperson") {
    const sp = (meta as { salesperson?: string }).salesperson ?? "";
    return t("Назначен по продавцу Zoho «{sp}»: {name}", { sp, name: target });
  }
  if (meta.sys === "invoice" || meta.sys === "payment") {
    const d = meta as { number?: string; total?: number; currency?: string; invoices?: string[] };
    const sum = money(d.total ?? 0, d.currency ?? "AED");
    if (meta.sys === "invoice") return t("Выставлен счёт {n} на {sum}", { n: d.number ?? "", sum });
    return d.invoices?.length ? t("Получена оплата {sum} по счёту {n}", { sum, n: d.invoices.join(", ") }) : t("Получена оплата {sum}", { sum });
  }
  if (meta.sys === "autoclose") {
    const a = meta as { after?: number; unit?: string };
    const unit = a.unit === "hours" ? t("ч") : a.unit === "weeks" ? t("нед.") : t("дн.");
    return t("Диалог закрыт автоматически: нет активности {n} {unit}", { n: a.after ?? "", unit });
  }
  return m.text;
}

/* Шапка письма в пузыре: тема, от кого/кому — чтобы переписка по почте читалась как диалог, но тема не терялась */
function EmailHead({ m, email, out }: { m: Message; email: NonNullable<ReturnType<typeof emailOf>>; out: boolean }) {
  const muted = out ? "text-primary-foreground/75" : "text-muted-foreground";
  return (
    <div className={cn("mb-1.5 border-b pb-1.5", out ? "border-primary-foreground/25" : "border-border/70")}>
      <p className="flex items-center gap-1.5 text-[12.5px] font-semibold leading-snug">
        <i className="ri-mail-line text-[13px]" aria-hidden />
        <span className="truncate">{email.subject || t("(без темы)")}</span>
      </p>
      <p className={cn("mt-0.5 truncate text-[10.5px]", muted)}>
        {out ? t("Кому: {to}", { to: [...email.to, ...email.cc].join(", ") }) : t("От: {from}", { from: email.fromName ? `${email.fromName} <${email.from}>` : email.from })}
        {m.history ? "" : ""}
      </p>
    </div>
  );
}

function Bubble({ m, quoted, bot, peer }: { m: Message; quoted?: Message | null; bot?: string | null; peer: string }) {
  if (m.kind === "system") {
    return (
      <div className="my-1 flex justify-center">
        <span className="flash-mono max-w-[90%] rounded-full bg-muted px-2.5 py-1 text-center text-[10.5px] text-muted-foreground">{systemText(m)}</span>
      </div>
    );
  }
  if (m.kind === "note") {
    return (
      <div className="max-w-[88%] self-start rounded-btn rounded-bl-sm border border-amber-400/40 bg-amber-400/10 px-3 py-2">
        <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-600">
          <StickyNote className="size-3" aria-hidden />
          {t("Заметка для команды")}{m.author ? ` · ${m.author}` : ""}
        </span>
        <p className="mt-1 whitespace-pre-wrap break-words text-[12px] leading-relaxed text-foreground/90">{m.text}</p>
        <span className="mt-1 block text-[10px] text-muted-foreground">{hm(m.sentAt)}</span>
      </div>
    );
  }
  const out = m.kind === "out";
  const email = emailOf(m);
  return (
    <div
      className={cn(
        "max-w-[88%] rounded-btn px-3 py-2",
        email && "w-full max-w-[96%]",
        out ? "self-end rounded-br-sm bg-primary text-primary-foreground" : "self-start rounded-bl-sm bg-muted text-foreground",
        m.status === "failed" && "ring-2 ring-red-400/70",
      )}
    >
      <span className={cn("mb-0.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide", out ? "justify-end text-primary-foreground/70" : "text-muted-foreground")} data-testid="message-author">
        <i className={cn("text-[11px]", out ? "ri-customer-service-2-line" : "ri-user-3-line")} aria-hidden />
        {out ? m.author || (m.fromPhone && bot ? bot : t("Сотрудник")) : email?.fromName || peer}
      </span>
      {quoted ? (
        <div className={cn("mb-1.5 border-s-2 ps-2 text-[11px] leading-snug", out ? "border-primary-foreground/50 text-primary-foreground/80" : "border-primary/50 text-muted-foreground")}>
          <span className="block truncate">{quoted.text || quoted.media?.name || t("Вложение")}</span>
        </div>
      ) : null}
      {m.media ? (
        <div className={cn(m.text && "mb-1.5")}>
          <Attachment m={m} out={out} />
        </div>
      ) : null}
      {email ? <EmailHead m={m} email={email} out={out} /> : null}
      {m.text ? <p className={cn("whitespace-pre-wrap break-words text-[12.5px] leading-relaxed", email && "max-h-72 overflow-y-auto")}>{m.text}</p> : null}
      {email && email.attachments.length ? (
        <ul className="mt-1.5 flex flex-col gap-1">
          {email.attachments.map((a) => (
            <li key={a.id}>
              <a
                href={`/api/conversations/${m.conversationId}/messages/${m.id}/attachments/${a.id}`}
                className={cn("inline-flex max-w-full items-center gap-1.5 rounded-[6px] border px-2 py-1 text-[11.5px]", out ? "border-primary-foreground/30 text-primary-foreground" : "border-border text-foreground hover:bg-background")}
              >
                <Paperclip className="size-3 flex-none" aria-hidden />
                <span className="truncate">{a.name}</span>
                {a.size ? <span className="flex-none opacity-70">{Math.max(1, Math.round(a.size / 1024))} KB</span> : null}
              </a>
            </li>
          ))}
        </ul>
      ) : null}
      <span className={cn("mt-1 flex items-center justify-end gap-1.5 text-[10px]", out ? "text-primary-foreground/70" : "text-muted-foreground")}>
        {out && m.fromPhone ? (
          <span className="rounded-full bg-primary-foreground/20 px-1.5 py-0.5 font-semibold text-primary-foreground">{!m.authorId && bot && m.author === bot ? t("бот") : t("с телефона")}</span>
        ) : null}
        {hm(m.sentAt)}
        {out ? <Status m={m} /> : null}
      </span>
    </div>
  );
}

export default function ChatPane({
  conversation,
  me,
  members,
  snippets = [],
  user = null,
  staff,
  readOnly,
  busy,
  onSend,
  onUpload,
  onForward,
  onAssign,
  onStatus,
  onCategory,
  rules = null,
  onBack,
  onOpenContact,
  onLoadMore,
}: {
  conversation: ConversationFull;
  me: string;
  members: Member[];
  /** может назначать на других и закрывать чужие */
  staff: boolean;
  readOnly: boolean;
  busy: boolean;
  onSend: (text: string, kind: SendKind, replyTo?: string | null, snippetId?: string | null) => Promise<void>;
  /** шаблоны ответов для вызова по «/» */
  snippets?: Snippet[];
  user?: { name: string; email: string } | null;
  onUpload: (file: File, caption: string) => Promise<void>;
  /** «Переслать» сообщение в другой диалог (toName — для уведомления) */
  onForward?: (messageId: number, toId: string, toName: string) => Promise<void>;
  onAssign: (userId: string | null) => void;
  onStatus: (status: "open" | "closed", note?: string) => void;
  onCategory?: (category: string) => void;
  rules?: InboxRules | null;
  onBack: () => void;
  onOpenContact?: () => void;
  onLoadMore?: () => void;
}) {
  const [draft, setDraft] = useState("");
  const [note, setNote] = useState(false);
  const [askBaza, setAskBaza] = useState(false);
  const bazaOn = useBazaEnabled();
  const [reply, setReply] = useState<Message | null>(null);
  const [files, setFiles] = useState<File[]>([]); // несколько файлов за раз — уходят по очереди, подпись к первому
  const file = files[0] ?? null;
  const setFile = (f: File | null) => setFiles(f ? [f] : []);
  /** «Переслать»: исходное сообщение, поиск диалога-получателя и найденные диалоги */
  const [forward, setForward] = useState<Message | null>(null);
  const [fwdQuery, setFwdQuery] = useState("");
  const [fwdItems, setFwdItems] = useState<Conversation[]>([]);
  /** выбранный шаблон: его файлы уйдут вместе с текстом; slash — открыт список по «/» */
  const [snippet, setSnippet] = useState<Snippet | null>(null);
  // клиент Zoho Books: связанные с контактом и подсказка по совпавшему номеру
  const [books, setBooks] = useState<{ linked: ZohoCustomer[]; suggested: ZohoCustomer[]; canLink: boolean } | null>(null);
  const [linking, setLinking] = useState("");
  const [booksError, setBooksError] = useState("");
  const [slash, setSlash] = useState<{ query: string; index: number } | null>(null);
  /** форма «заметка при закрытии» (если правило требует заметку) */
  const [closeNote, setCloseNote] = useState<string | null>(null);
  const feed = useRef<HTMLDivElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const stick = useRef(true);

  const msgs = conversation.messages;
  const byExt = new Map(msgs.filter((m) => m.externalId).map((m) => [m.externalId as string, m]));
  const lastStatus = msgs[msgs.length - 1]?.status;

  useEffect(() => {
    setBooks(null);
    setBooksError("");
    conversationsApi.books(conversation.id).then(setBooks, () => setBooks(null));
  }, [conversation.id, conversation.contactId]);

  const linkBooks = async (z: ZohoCustomer) => {
    if (!conversation.contactId) return;
    setLinking(z.booksId);
    try {
      await zohoApi.link(z.booksId, { contactId: conversation.contactId });
      setBooks(await conversationsApi.books(conversation.id));
    } catch (e) {
      setBooksError(apiErrorText(e));
    } finally {
      setLinking("");
    }
  };

  useEffect(() => {
    const el = feed.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [conversation.id, msgs.length, lastStatus]);

  useEffect(() => {
    setDraft("");
    setNote(false);
    setReply(null);
    setFiles([]);
    setSnippet(null);
    setSlash(null);
    setCloseNote(null);
    setForward(null);
    stick.current = true;
  }, [conversation.id]);

  // поиск диалога-получателя для «Переслать» (открытые диалоги, свежие сверху)
  useEffect(() => {
    if (!forward) return;
    const h = setTimeout(() => {
      conversationsApi
        .list({ q: fwdQuery.trim(), limit: 8, sort: "new" })
        .then((r) => setFwdItems(r.items.filter((c) => !c.archive)))
        .catch(() => setFwdItems([]));
    }, 250);
    return () => clearTimeout(h);
  }, [forward, fwdQuery]);

  const slashItems = slash
    ? snippets.filter((s) => !slash.query || s.key.startsWith(slash.query) || s.name.toLowerCase().includes(slash.query)).slice(0, 8)
    : [];

  const onDraftChange = (value: string) => {
    setDraft(value);
    const m = /^\/([^\s/]*)$/.exec(value);
    setSlash(m && !note ? { query: m[1].toLowerCase(), index: 0 } : null);
  };

  const pickSnippet = (s: Snippet) => {
    setDraft(fillVars(s.message, conversation.contact, user));
    setSnippet(s);
    setSlash(null);
    setFile(null);
    textarea.current?.focus();
  };

  const submit = async () => {
    const text = draft.trim();
    if (files.length) {
      for (const [i, f] of files.entries()) await onUpload(f, i === 0 ? text : "");
      setFiles([]);
      setDraft("");
      return;
    }
    const withFiles = Boolean(snippet && !note && snippet.files.length > 0);
    if (!text && !withFiles) return;
    await onSend(text, note ? "note" : "out", reply?.externalId ?? null, note ? null : snippet?.id ?? null);
    setDraft("");
    setNote(false);
    setReply(null);
    setSnippet(null);
    textarea.current?.focus();
  };

  const canSend = !readOnly && !busy && (Boolean(draft.trim()) || files.length > 0 || Boolean(snippet && !note && snippet.files.length > 0));
  const closed = conversation.status === "closed";
  const mineOrFree = conversation.assigneeId === me || conversation.assigneeId === null;
  const contactLine = conversation.phone || conversation.externalId;

  return (
    <div className="flex min-h-0 flex-col">
      {/* шапка диалога */}
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2.5">
        <button
          type="button"
          onClick={onBack}
          aria-label={t("К списку диалогов")}
          className="grid size-7 flex-none cursor-pointer place-items-center rounded-btn border border-border text-muted-foreground transition hover:bg-muted lg:hidden"
        >
          <ArrowLeft className="size-3.5" aria-hidden />
        </button>
        <span className="relative flex-none">
          <span className="grid size-8 place-items-center rounded-full bg-primary/15 text-[11px] font-bold text-primary" aria-hidden>
            {initials(conversation.name)}
          </span>
          <span className="absolute -bottom-1 -end-1 grid size-4 place-items-center rounded-full bg-card text-[9px] leading-none text-emerald-500 ring-1 ring-border" aria-hidden>
            <i className={channelIcon(conversation.channel)} />
          </span>
        </span>
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-1.5 truncate text-[12.5px] font-semibold">
            {conversation.contactId && onOpenContact ? (
              <button type="button" onClick={onOpenContact} className="cursor-pointer truncate text-start hover:text-primary" title={t("Открыть карточку контакта")}>
                {conversation.name}
              </button>
            ) : (
              conversation.name
            )}
            {closed ? <span className="rounded-full bg-muted px-1.5 py-0.5 text-[9.5px] font-semibold text-muted-foreground">{t("Закрыт")}</span> : null}
            {!conversation.contactId ? (
              <span className="rounded-full bg-amber-400/15 px-1.5 py-0.5 text-[9.5px] font-semibold text-amber-600" title={t("Номер не найден среди контактов")}>
                {t("без контакта")}
              </span>
            ) : null}
            {conversation.salesperson && !conversation.assigneeId ? (
              <span className="rounded-full bg-sky-500/10 px-1.5 py-0.5 text-[9.5px] font-semibold text-sky-600" title={t("Продавец по данным Zoho; сотрудник Hub ещё не привязан — Настройки → Zoho → «Продавцы Zoho»")}>
                {t("продавец Zoho: {name}", { name: conversation.salesperson })}
              </span>
            ) : null}
            {conversation.baza?.status === "sent" ? (
              <a
                href={conversation.baza.url ?? undefined}
                target="_blank"
                rel="noreferrer"
                title={conversation.baza.summary || (conversation.baza.analysisStatus === "done" ? t("Оценка диалога в базе знаний") : t("База знаний ещё оценивает диалог"))}
                className={cn(
                  "rounded-full px-1.5 py-0.5 text-[9.5px] font-semibold",
                  conversation.baza.score == null ? "bg-muted text-muted-foreground" : conversation.baza.score >= 7 ? "bg-emerald-500/15 text-emerald-600" : conversation.baza.score >= 4 ? "bg-amber-400/15 text-amber-600" : "bg-red-500/15 text-red-500",
                )}
              >
                {conversation.baza.score == null ? t("база знаний: оценивается") : t("база знаний: {score}/10", { score: conversation.baza.score })}
              </a>
            ) : null}
            {conversation.sentiment ? (
              <span
                title={conversation.summary || t("Тональность по оценке ИИ")}
                className={cn(
                  "rounded-full px-1.5 py-0.5 text-[9.5px] font-semibold",
                  conversation.sentiment === "positive" ? "bg-emerald-500/15 text-emerald-600" : conversation.sentiment === "negative" ? "bg-red-500/15 text-red-500" : "bg-muted text-muted-foreground",
                )}
              >
                {conversation.sentiment === "positive" ? t("позитив") : conversation.sentiment === "negative" ? t("негатив") : t("нейтрально")}
              </span>
            ) : null}
          </p>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[10.5px] text-muted-foreground">
            <span className="inline-flex items-center gap-1 whitespace-nowrap font-semibold text-foreground/80">{contactLine}</span>
            {conversation.company ? (
              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                <i className="ri-building-line text-[11px]" aria-hidden />
                {conversation.company}
              </span>
            ) : null}
            {conversation.channelName ? (
              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                <i className={cn(channelIcon(conversation.channel), "text-[11px]")} aria-hidden />
                {conversation.channelName}
              </span>
            ) : null}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {!readOnly && conversation.assigneeId !== me && (staff || mineOrFree) ? (
            <button
              type="button"
              onClick={() => onAssign(me)}
              title={conversation.bot ? t("Диалог ведёт бот {name}. Перехватить: диалог и клиент переходят к вам", { name: conversation.bot }) : t("Диалог и клиент переходят к вам")}
              className="inline-flex h-7 cursor-pointer items-center gap-1 whitespace-nowrap rounded-btn bg-primary px-2.5 text-[11px] font-semibold text-primary-foreground transition hover:bg-primary/90"
            >
              <i className={cn(conversation.bot ? "ri-robot-2-line" : "ri-hand-heart-line", "text-[12px] leading-none")} aria-hidden />
              {conversation.bot ? t("Перехватить") : t("Взять себе")}
            </button>
          ) : null}
          <span className="relative inline-flex items-center">
            <select
              value={conversation.assigneeId ?? ""}
              disabled={readOnly || (!staff && !mineOrFree)}
              onChange={(e) => onAssign(e.target.value || null)}
              aria-label={t("Ответственный")}
              title={t("Ответственный за диалог")}
              className="h-7 max-w-[11rem] cursor-pointer appearance-none truncate rounded-btn border border-border bg-background pe-6 ps-2 text-[11px] font-medium text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-default disabled:opacity-70"
            >
              <option value="">{conversation.bot ? t("бот {name}", { name: conversation.bot }) : t("Не назначен")}</option>
              {staff
                ? members.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))
                : [
                    <option key={me} value={me}>
                      {members.find((m) => m.id === me)?.name ?? t("Я")}
                    </option>,
                    ...(conversation.assigneeId && conversation.assigneeId !== me
                      ? [
                          <option key={conversation.assigneeId} value={conversation.assigneeId}>
                            {conversation.assignee ?? t("Другой сотрудник")}
                          </option>,
                        ]
                      : []),
                  ]}
            </select>
            <i className="ri-arrow-down-s-line pointer-events-none absolute end-1 top-1/2 -translate-y-1/2 text-[12px] leading-none text-muted-foreground" aria-hidden />
          </span>
          {rules && rules.categories.length > 0 ? (
            <span className="relative inline-flex items-center">
              <select
                value={conversation.category}
                disabled={readOnly || !onCategory}
                onChange={(e) => onCategory?.(e.target.value)}
                aria-label={t("Категория диалога")}
                title={t("Категория диалога")}
                className="h-7 max-w-[9rem] cursor-pointer appearance-none truncate rounded-btn border border-border bg-background pe-6 ps-2 text-[11px] font-medium text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-default disabled:opacity-70"
              >
                <option value="">{t("Без категории")}</option>
                {rules.categories.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
                {conversation.category && !rules.categories.some((c) => c.name === conversation.category) ? <option value={conversation.category}>{conversation.category}</option> : null}
              </select>
              <i className="ri-arrow-down-s-line pointer-events-none absolute end-1 top-1/2 -translate-y-1/2 text-[12px] leading-none text-muted-foreground" aria-hidden />
            </span>
          ) : null}
          {!readOnly && (staff || mineOrFree) ? (
            <button
              type="button"
              onClick={() => (closed ? onStatus("open") : rules?.requireNote ? setCloseNote((v) => (v === null ? "" : null)) : onStatus("closed"))}
              className={cn(
                "inline-flex h-7 cursor-pointer items-center gap-1 whitespace-nowrap rounded-btn border px-2 text-[11px] font-semibold transition",
                closed ? "border-primary/40 text-primary hover:bg-primary/10" : "border-border text-muted-foreground hover:text-foreground",
              )}
            >
              <i className={closed ? "ri-chat-new-line" : "ri-checkbox-circle-line"} aria-hidden />
              {closed ? t("Открыть снова") : t("Закрыть")}
            </button>
          ) : null}
        </div>
      </div>
      {books && (books.linked.length > 0 || books.suggested.length > 0) ? (
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-border bg-muted/40 px-3 py-1.5 text-[10.5px] text-muted-foreground">
          <i className="ri-bill-line text-[12px] text-primary" aria-hidden />
          {books.linked.length > 0 ? (
            <>
              <span className="font-semibold text-foreground/80">{t("Клиент Books:")}</span>
              {books.linked.map((z) => (
                <a key={z.booksId} href={z.booksUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 whitespace-nowrap hover:text-primary hover:underline">
                  {z.companyName || z.name}
                  <span className="opacity-70">
                    · {aed(z.revenue)} · {t("{n} счетов", { n: z.invoices })}
                    {z.receivable > 0 ? ` · ${t("долг {sum}", { sum: aed(z.receivable) })}` : ""}
                  </span>
                </a>
              ))}
            </>
          ) : (
            books.suggested.map((z) => (
              <span key={z.booksId} className="inline-flex flex-wrap items-center gap-1.5">
                <span>{t("Похоже, это клиент Books «{name}» — совпал телефон.", { name: z.companyName || z.name })}</span>
                {books.canLink && !readOnly ? (
                  <button
                    type="button"
                    disabled={linking === z.booksId}
                    onClick={() => void linkBooks(z)}
                    className="inline-flex h-6 cursor-pointer items-center gap-1 whitespace-nowrap rounded-btn border border-primary/40 px-2 text-[10.5px] font-semibold text-primary transition hover:bg-primary/10 disabled:opacity-50"
                  >
                    <i className="ri-links-line text-[11px]" aria-hidden />
                    {t("Связать")}
                  </button>
                ) : null}
              </span>
            ))
          )}
          {booksError ? <span className="text-red-500">{booksError}</span> : null}
        </div>
      ) : null}

      {closeNote !== null && !closed ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!closeNote.trim()) return;
            onStatus("closed", closeNote.trim());
            setCloseNote(null);
          }}
          className="flex flex-col gap-1.5 border-b border-amber-400/40 bg-amber-400/10 px-3 py-2.5"
        >
          <p className="text-[11px] font-semibold text-amber-700">{t("Чем закончился диалог? Заметка обязательна при закрытии — её увидит только команда.")}</p>
          <textarea
            value={closeNote}
            onChange={(e) => setCloseNote(e.target.value)}
            rows={2}
            autoFocus
            maxLength={5000}
            placeholder={t("Например: клиент взял паузу, вернётся после праздников")}
            className="min-h-12 w-full resize-y rounded-btn border border-amber-400/60 bg-background px-2.5 py-1.5 text-[12px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-amber-400/25"
          />
          <div className="flex items-center justify-end gap-1.5">
            <button type="button" onClick={() => setCloseNote(null)} className="inline-flex h-7 cursor-pointer items-center rounded-btn border border-border px-2.5 text-[11px] font-semibold text-muted-foreground hover:text-foreground">
              {t("Отмена")}
            </button>
            <button type="submit" disabled={!closeNote.trim()} className="inline-flex h-7 cursor-pointer items-center gap-1 rounded-btn bg-primary px-2.5 text-[11px] font-semibold text-primary-foreground disabled:opacity-50">
              <i className="ri-checkbox-circle-line" aria-hidden />
              {t("Закрыть диалог")}
            </button>
          </div>
        </form>
      ) : null}

      {/* лента */}
      <div
        ref={feed}
        onScroll={(e) => {
          const el = e.currentTarget;
          stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
          if (el.scrollTop < 30 && conversation.hasMore && onLoadMore) onLoadMore();
        }}
        className="flex h-[320px] flex-col gap-2 overflow-y-auto bg-background/40 p-3 md:h-[380px]"
      >
        {conversation.hasMore ? <p className="text-center text-[10px] text-muted-foreground">{t("Прокрутите вверх, чтобы загрузить более ранние сообщения")}</p> : null}
        {msgs.length === 0 ? <p className="m-auto text-center text-[11.5px] text-muted-foreground">{t("Сообщений пока нет — напишите первым.")}</p> : null}
        {msgs.map((m, i) => {
          const prev = msgs[i - 1];
          const newDay = !prev || new Date(prev.sentAt).toDateString() !== new Date(m.sentAt).toDateString();
          return (
            <div key={m.id} className="contents">
              {newDay ? (
                <div className="my-1 flex justify-center">
                  <span className="rounded-full bg-background px-2.5 py-0.5 text-[10px] font-medium text-muted-foreground ring-1 ring-border">{dayLabel(m.sentAt)}</span>
                </div>
              ) : null}
              <div
                className={cn("group flex flex-col", m.kind === "out" ? "items-end" : "items-start")}
                onDoubleClick={() => {
                  if (m.externalId && m.kind !== "note" && !readOnly) setReply(m);
                }}
              >
                <Bubble m={m} bot={conversation.bot} peer={conversation.name} quoted={m.quotedExternalId ? byExt.get(m.quotedExternalId) ?? null : null} />
                {m.kind !== "note" && m.kind !== "system" && !readOnly ? (
                  <span className="mt-0.5 hidden items-center gap-0.5 group-hover:inline-flex" data-testid="message-actions">
                    {m.externalId && !conversation.archive && conversation.channel !== "email" ? (
                      <button type="button" onClick={() => setReply(m)} title={t("Ответить")} className="grid size-6 cursor-pointer place-items-center rounded text-muted-foreground hover:bg-muted hover:text-foreground">
                        <i className="ri-reply-line text-[13px]" aria-hidden />
                      </button>
                    ) : null}
                    {onForward && (m.text.trim() || m.media?.url) ? (
                      <button
                        type="button"
                        onClick={() => {
                          setForward(m);
                          setFwdQuery("");
                        }}
                        title={t("Переслать в другой диалог")}
                        className="grid size-6 cursor-pointer place-items-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                        data-testid="message-forward"
                      >
                        <i className="ri-share-forward-line text-[13px]" aria-hidden />
                      </button>
                    ) : null}
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {/* поле ответа */}
      <div className="border-t border-border p-2.5">
        {forward && onForward ? (
          <div className="mb-1.5 rounded-btn border border-primary/40 bg-primary/5 p-2 text-[11px]" data-testid="forward-picker">
            <div className="flex items-center gap-2">
              <i className="ri-share-forward-line text-primary" aria-hidden />
              <span className="min-w-0 flex-1 truncate text-muted-foreground">
                {t("Переслать:")} <span className="text-foreground">{forward.text || forward.media?.name || t("вложение")}</span>
              </span>
              <button type="button" onClick={() => setForward(null)} aria-label={t("Не пересылать")} className="grid size-5 cursor-pointer place-items-center rounded text-muted-foreground hover:text-foreground">
                <X className="size-3" aria-hidden />
              </button>
            </div>
            <input
              value={fwdQuery}
              onChange={(e) => setFwdQuery(e.target.value)}
              autoFocus
              placeholder={t("Кому: имя, компания или телефон")}
              className="mt-1.5 h-8 w-full rounded-btn border border-border bg-background px-2.5 text-[12px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
              data-testid="forward-search"
            />
            <div className="mt-1 flex max-h-40 flex-col overflow-y-auto">
              {fwdItems.length === 0 ? <p className="px-1 py-1.5 text-muted-foreground">{t("Диалогов не найдено")}</p> : null}
              {fwdItems.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    const msg = forward;
                    setForward(null);
                    void onForward(msg.id, c.id, c.name);
                  }}
                  className="flex cursor-pointer items-center gap-2 rounded-btn px-2 py-1.5 text-start transition hover:bg-muted disabled:opacity-50"
                >
                  <i className={cn(c.channel === "telegram" ? "ri-telegram-line text-sky-600" : c.channel === "email" ? "ri-mail-line text-muted-foreground" : "ri-whatsapp-line text-emerald-600", "text-[13px]")} aria-hidden />
                  <span className="min-w-0 flex-1 truncate text-[12px] font-semibold">{c.name}</span>
                  <span className="truncate text-[10.5px] text-muted-foreground">{c.company || c.phone || ""}</span>
                  {c.id === conversation.id ? <span className="text-[10px] text-muted-foreground">{t("этот диалог")}</span> : null}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {reply ? (
          <div className="mb-1.5 flex items-center gap-2 rounded-btn border border-primary/40 bg-primary/5 px-2.5 py-1.5 text-[11px]">
            <i className="ri-reply-line text-primary" aria-hidden />
            <span className="min-w-0 flex-1 truncate text-muted-foreground">
              {t("Ответ на:")} <span className="text-foreground">{reply.text || reply.media?.name || t("вложение")}</span>
            </span>
            <button type="button" onClick={() => setReply(null)} aria-label={t("Не отвечать на сообщение")} className="grid size-5 cursor-pointer place-items-center rounded text-muted-foreground hover:text-foreground">
              <X className="size-3" aria-hidden />
            </button>
          </div>
        ) : null}
        {files.map((f, i) => (
          <div key={`${f.name}-${i}`} className="mb-1.5 flex items-center gap-2 rounded-btn border border-border bg-background px-2.5 py-1.5 text-[11px]">
            <Paperclip className="size-3.5 text-primary" aria-hidden />
            <span className="min-w-0 flex-1 truncate">
              {f.name} <span className="text-muted-foreground">· {size(f.size)}</span>
              {conversation.channel === "whatsapp" && /^(image|video|audio)\//.test(f.type) && f.size > WA_MEDIA_MB * 1024 * 1024 ? (
                <span className="ms-1 text-amber-600">{t("больше {n} МБ — WhatsApp получит как документ", { n: WA_MEDIA_MB })}</span>
              ) : null}
            </span>
            <button type="button" onClick={() => setFiles((prev) => prev.filter((_, k) => k !== i))} aria-label={t("Убрать файл")} className="grid size-5 cursor-pointer place-items-center rounded text-muted-foreground hover:text-foreground">
              <X className="size-3" aria-hidden />
            </button>
          </div>
        ))}
        {snippet && !note && snippet.files.length > 0 ? (
          <div className="mb-1.5 flex flex-wrap items-center gap-1.5 rounded-btn border border-border bg-background px-2.5 py-1.5 text-[11px]">
            <i className="ri-file-copy-2-line text-primary" aria-hidden />
            <span className="text-muted-foreground">{t("Файлы шаблона «{name}»:", { name: snippet.name })}</span>
            {snippet.files.map((f) => (
              <span key={f.id} className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5">
                <Paperclip className="size-3 text-muted-foreground" aria-hidden />
                <span className="max-w-[10rem] truncate">{f.name}</span>
              </span>
            ))}
            <button type="button" onClick={() => setSnippet(null)} aria-label={t("Не прикладывать файлы шаблона")} className="ms-auto grid size-5 cursor-pointer place-items-center rounded text-muted-foreground hover:text-foreground">
              <X className="size-3" aria-hidden />
            </button>
          </div>
        ) : null}
        {slash ? (
          <div role="listbox" aria-label={t("Шаблоны ответов")} className="mb-1.5 max-h-56 overflow-y-auto rounded-btn border border-border bg-card p-1 shadow-lift">
            {slashItems.length === 0 ? (
              <p className="px-2.5 py-2 text-[11px] text-muted-foreground">
                {snippets.length === 0 ? t("Шаблонов пока нет — их заводят в «Настройки → Шаблоны».") : t("Нет шаблонов по запросу «/{q}»", { q: slash.query })}
              </p>
            ) : (
              slashItems.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  role="option"
                  aria-selected={i === slash.index}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickSnippet(s)}
                  className={cn(
                    "flex w-full cursor-pointer items-start gap-2 rounded-btn px-2.5 py-1.5 text-start transition",
                    i === slash.index ? "bg-primary/10" : "hover:bg-muted",
                  )}
                >
                  <span className="flash-mono mt-0.5 flex-none text-[11px] text-primary">/{s.key}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12px] font-semibold">{s.name}</span>
                    <span className="block truncate text-[11px] text-muted-foreground">{s.message || t("только файлы")}</span>
                  </span>
                  {s.files.length > 0 ? (
                    <span className="inline-flex flex-none items-center gap-0.5 text-[10.5px] text-muted-foreground">
                      <Paperclip className="size-3" aria-hidden />
                      {s.files.length}
                    </span>
                  ) : null}
                </button>
              ))
            )}
          </div>
        ) : null}
        {conversation.archive ? (
          <p className="px-1 py-2 text-center text-[11px] text-muted-foreground">
            {t("Архив Wazzup из Zoho CRM: только чтение. Если клиент напишет снова, диалог появится во «Входящих» подключённого номера.")}
          </p>
        ) : readOnly ? (
          <p className="px-1 py-2 text-center text-[11px] text-muted-foreground">{t("Режим просмотра: отвечать в диалогах может сотрудник с правом записи.")}</p>
        ) : (
          <>
            {askBaza ? (
              <BazaAsk
                conversationId={conversation.id}
                onClose={() => setAskBaza(false)}
                onInsert={(text) => {
                  setNote(false);
                  setDraft((d) => (d.trim() ? `${d.trimEnd()}\n\n${text}` : text));
                  textarea.current?.focus();
                }}
              />
            ) : null}
          <div className="flex items-end gap-1.5">
            {bazaOn ? (
              <button
                type="button"
                onClick={() => setAskBaza((v) => !v)}
                aria-pressed={askBaza}
                title={t("Спросить базу знаний: цены, наличие, договоры и счета клиента, реквизиты — ответ можно вставить клиенту")}
                aria-label={t("Спросить базу знаний")}
                className={cn(
                  "grid size-9 flex-none cursor-pointer place-items-center rounded-btn border transition",
                  askBaza ? "border-sky-500/60 bg-sky-500/15 text-sky-600" : "border-border text-muted-foreground hover:bg-muted",
                )}
                data-testid="baza-ask-toggle"
              >
                <BookOpen className="size-3.5" aria-hidden />
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setNote((v) => !v)}
              aria-pressed={note}
              title={t("Внутренняя заметка: клиент её не увидит")}
              className={cn(
                "grid size-9 flex-none cursor-pointer place-items-center rounded-btn border transition",
                note ? "border-amber-400/60 bg-amber-400/15 text-amber-600" : "border-border text-muted-foreground hover:bg-muted",
              )}
            >
              <StickyNote className="size-3.5" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={note}
              title={t("Отправить файлы: фото, видео, голосовое или документ любого формата, до {n} МБ каждый; можно несколько сразу", { n: MAX_UPLOAD_MB })}
              aria-label={t("Прикрепить файл")}
              className="grid size-9 flex-none cursor-pointer place-items-center rounded-btn border border-border text-muted-foreground transition hover:bg-muted disabled:opacity-40"
            >
              <Paperclip className="size-3.5" aria-hidden />
            </button>
            <input
              ref={fileInput}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                const picked = Array.from(e.target.files ?? []);
                if (picked.length) {
                  setFiles((prev) => [...prev, ...picked].slice(0, 10));
                  setNote(false);
                }
                e.target.value = "";
              }}
            />
            <textarea
              ref={textarea}
              value={draft}
              onChange={(e) => onDraftChange(e.target.value)}
              onKeyDown={(e) => {
                if (slash) {
                  if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                    e.preventDefault();
                    const n = slashItems.length || 1;
                    setSlash({ ...slash, index: (slash.index + (e.key === "ArrowDown" ? 1 : n - 1)) % n });
                    return;
                  }
                  if ((e.key === "Enter" || e.key === "Tab") && slashItems[slash.index]) {
                    e.preventDefault();
                    pickSnippet(slashItems[slash.index]);
                    return;
                  }
                  if (e.key === "Escape") {
                    setSlash(null);
                    return;
                  }
                }
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void submit();
                }
              }}
              rows={1}
              maxLength={20000}
              placeholder={
                note
                  ? t("Внутренняя заметка — клиент её не увидит. @имя — упомянуть коллегу")
                  : files.length
                    ? t("Подпись к файлу (необязательно)")
                    : conversation.channel === "email"
                      ? t("Ответить письмом от имени ящика · Enter — отправить, Shift+Enter — перенос, «/» — шаблон")
                      : t("Ответить в {channel} · Enter — отправить, Shift+Enter — перенос, «/» — шаблон", { channel: channelLabel(conversation.channel) })
              }
              className={cn(
                "max-h-32 min-h-9 min-w-0 flex-1 resize-y box-border rounded-btn border bg-background px-2.5 py-2 text-[12.5px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2",
                note ? "border-amber-400/60 focus:ring-amber-400/25" : "border-border focus:border-primary focus:ring-primary/25",
              )}
            />
            <button
              type="button"
              onClick={() => void submit()}
              disabled={!canSend}
              aria-label={note ? t("Сохранить заметку") : t("Отправить сообщение")}
              className="grid size-9 flex-none cursor-pointer place-items-center rounded-btn bg-primary text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-45"
            >
              {busy ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : <Send className="size-3.5" aria-hidden />}
            </button>
          </div>
          </>
        )}
      </div>
    </div>
  );
}
